import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, XCircle, Package, Home } from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { useCart } from '../context/CartContext';

export default function CheckoutSuccessPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { fetchCart } = useCart();
    
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [paymentData, setPaymentData] = useState(null);

    const sessionId = searchParams.get('session_id');

    useEffect(() => {
        if (sessionId) {
            pollPaymentStatus();
        } else {
            setStatus('error');
        }
    }, [sessionId]);

    const pollPaymentStatus = async (attempts = 0) => {
        const maxAttempts = 5;
        const pollInterval = 2000;

        if (attempts >= maxAttempts) {
            setStatus('error');
            return;
        }

        try {
            const data = await apiRequest(`/checkout/status/${sessionId}`);
            setPaymentData(data);

            if (data.payment_status === 'paid') {
                setStatus('success');
                await fetchCart(); // Refresh cart (should be empty)
                return;
            } else if (data.status === 'expired') {
                setStatus('error');
                return;
            }

            // Continue polling
            setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } catch (error) {
            console.error('Payment status check failed:', error);
            if (attempts >= maxAttempts - 1) {
                setStatus('error');
            } else {
                setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
            }
        }
    };

    return (
        <div className="min-h-screen pt-20 pb-12 flex items-center justify-center" data-testid="checkout-success-page">
            <div className="max-w-md mx-auto px-4 w-full">
                <Card className="glass rounded-3xl overflow-hidden">
                    <CardContent className="p-8 text-center">
                        {status === 'loading' && (
                            <>
                                <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                                    <Loader2 className="h-10 w-10 text-primary animate-spin" />
                                </div>
                                <h1 className="font-heading text-2xl font-bold mb-2">
                                    Vérification du paiement...
                                </h1>
                                <p className="text-muted-foreground">
                                    Veuillez patienter pendant que nous confirmons votre paiement.
                                </p>
                            </>
                        )}

                        {status === 'success' && (
                            <>
                                <div className="h-20 w-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
                                    <CheckCircle className="h-10 w-10 text-emerald-500" />
                                </div>
                                <h1 className="font-heading text-2xl font-bold mb-2 text-emerald-500">
                                    Paiement réussi !
                                </h1>
                                <p className="text-muted-foreground mb-6">
                                    Merci pour votre commande. Vous recevrez un email de confirmation.
                                </p>
                                <div className="space-y-3">
                                    <Link to="/orders" className="block">
                                        <Button className="w-full rounded-full" data-testid="view-orders-btn">
                                            <Package className="h-4 w-4 mr-2" />
                                            Voir mes commandes
                                        </Button>
                                    </Link>
                                    <Link to="/" className="block">
                                        <Button variant="secondary" className="w-full rounded-full">
                                            <Home className="h-4 w-4 mr-2" />
                                            Retour à l'accueil
                                        </Button>
                                    </Link>
                                </div>
                            </>
                        )}

                        {status === 'error' && (
                            <>
                                <div className="h-20 w-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                                    <XCircle className="h-10 w-10 text-red-500" />
                                </div>
                                <h1 className="font-heading text-2xl font-bold mb-2 text-red-500">
                                    Problème de paiement
                                </h1>
                                <p className="text-muted-foreground mb-6">
                                    Une erreur est survenue lors de la vérification de votre paiement. 
                                    Veuillez réessayer ou contacter le support.
                                </p>
                                <div className="space-y-3">
                                    <Link to="/cart" className="block">
                                        <Button className="w-full rounded-full" data-testid="retry-btn">
                                            Réessayer
                                        </Button>
                                    </Link>
                                    <Link to="/" className="block">
                                        <Button variant="secondary" className="w-full rounded-full">
                                            Retour à l'accueil
                                        </Button>
                                    </Link>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
